import { components } from '~/api/OnPremAgentClient/generated-schema'
import { JsonSchema } from '~/types/JsonSchema.interface'
import OnPremAgentAPI from '../OnPremAgentClient/OnPremAgentAPI'

export type GetValidInstrumentTypesResponse =
  components['schemas']['GetValidInstrumentTypesResponse']

export type InstrumentType = components['schemas']['InstrumentType']
export type DataIngestor = components['schemas']['DataIngestor']
export type DefaultSuccessResponse = components['schemas']['DefaultSuccessResponse']
export type GenerateTestDataRequest =
  components['schemas']['CytationGenerateTestDataRequest']
export type UploadDatasetRequest = components['schemas']['UploadDatasetRequest']

export type DataIngestorConfigDto =
  | components['schemas']['CytationDataIngestorConfigDto']
  | components['schemas']['ImageXpressDataIngestorConfigDto']
  | components['schemas']['ByonoyDataIngestorConfigDto']
  | components['schemas']['YokogawaCQ1DataIngestorConfigDto']

export type UpdateConfigRequest = components['schemas']['UpdateConfigRequest']

class DataIngestorsAPI extends OnPremAgentAPI {
  getValidInstrumentTypes = (): Promise<GetValidInstrumentTypesResponse> => {
    return this.client.GET('/api/ingestors/valid-instrument-types')
  }
  createDataIngestor = (
    instrumentName: string,
    configDto: DataIngestorConfigDto,
  ): Promise<DataIngestor> => {
    return this.client.POST('/api/ingestors/', {
      body: {
        instrument_name: instrumentName,
        config: configDto,
      },
    })
  }
  listDataIngestors = (): Promise<DataIngestor[]> => {
    return this.client.GET('/api/ingestors/')
  }
  getDataIngestor = (ingestorUuid: string): Promise<DataIngestor> => {
    return this.client.GET(`/api/ingestors/{ingestor_uuid}`, {
      params: {
        path: { ingestor_uuid: ingestorUuid },
      },
    })
  }
  startDataIngestor = (ingestorUuid: string): Promise<DataIngestor> => {
    return this.client.POST(`/api/ingestors/{ingestor_uuid}/start`, {
      params: {
        path: { ingestor_uuid: ingestorUuid },
      },
    })
  }
  stopDataIngestor = (ingestorUuid: string): Promise<DefaultSuccessResponse> => {
    return this.client.POST(`/api/ingestors/{ingestor_uuid}/stop`, {
      params: {
        path: { ingestor_uuid: ingestorUuid },
      },
    })
  }
  deleteDataIngestor = (ingestorUuid: string): Promise<DefaultSuccessResponse> => {
    return this.client.DELETE(`/api/ingestors/{ingestor_uuid}/delete`, {
      params: {
        path: { ingestor_uuid: ingestorUuid },
      },
    })
  }
  getDataIngestorConfigSchema = async (
    instrumentType: InstrumentType,
  ): Promise<JsonSchema> => {
    const response: Record<string, unknown> = await this.client.GET(
      `/api/ingestors/{instrument_type}/config-schema`,
      {
        params: {
          path: { instrument_type: instrumentType },
        },
      },
    )

    return response as unknown as JsonSchema
  }
  getGenerateTestDataRequestSchema = async (
    ingestorUuid: string,
  ): Promise<JsonSchema> => {
    const response: Record<string, unknown> = await this.client.GET(
      `/api/ingestors/{ingestor_uuid}/get-generate-test-data-request-schema`,
      {
        params: {
          path: { ingestor_uuid: ingestorUuid },
        },
      },
    )

    return response as unknown as JsonSchema
  }
  generateTestData = (
    ingestorUuid: string,
    request: GenerateTestDataRequest,
  ): Promise<DefaultSuccessResponse> => {
    return this.client.POST(`/api/ingestors/{ingestor_uuid}/generate-test-data`, {
      params: {
        path: { ingestor_uuid: ingestorUuid },
      },
      body: request,
    })
  }
  uploadDataset = (
    ingestorUuid: string,
    request: UploadDatasetRequest,
  ): Promise<DefaultSuccessResponse> => {
    return this.client.POST(`/api/ingestors/{ingestor_uuid}/upload-dataset`, {
      params: { path: { ingestor_uuid: ingestorUuid } },
      body: request,
    })
  }
  updateDataIngestorConfig = (
    ingestorUuid: string,
    configDto: UpdateConfigRequest,
  ): Promise<DataIngestor> => {
    return this.client.POST(`/api/ingestors/{ingestor_uuid}/update-config`, {
      params: {
        path: { ingestor_uuid: ingestorUuid },
      },
      body: configDto,
    })
  }
}

export default DataIngestorsAPI
